<script setup>
import { useStore } from 'vuex';
import { watch, computed, ref } from 'vue';
import MyLoadsDesktopTableComponent from '@/components/myLoads/MyLoadsDesktopTableComponent.vue';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
import { useRouter } from 'vue-router';
import MyLoadsDesktopFiltersComponent from '@/components/myLoads/MyLoadsDesktopFiltersComponent.vue';
import TeleportModalWrapperComponent from '@/components/TeleportModalWrapperComponent.vue';
import MyLoadsMobileMain from '@/components/myLoads/MyLoadsMobileMain.vue';
import { useScreenSize } from '@/composables/useScreenSize';

const { screenSize } = useScreenSize();

const store = useStore();
const router = useRouter();
const name = computed(() => router.currentRoute.value.name);

const emit = defineEmits(['connectEldClick']);

const myLoadsTabs = {
  unassigned: { title: 'Action Needed' },
  dispatched: { title: 'Covered' },
  'in-transit': { title: 'In Transit' },
  completed: { title: 'Completed' },
};
const pageTabs = ref(myLoadsTabs);
const activePageTab = computed(
  () => router.currentRoute.value.query.tab || Object.keys(pageTabs.value)[0]
);
// const pagination = computed(
//   () => store.state.myLoadsStore[activePageTab.value].pagination
// );
const isLoading = computed(
  () => store.state.myLoadsStore[activePageTab.value].isLoading
);
const loads = computed(() => {
  switch (activePageTab.value) {
    case 'unassigned':
      return store.state.myLoadsStore.unassigned.loads;
    case 'dispatched':
      return store.state.myLoadsStore.dispatched.loads;
    case 'in-transit':
      return store.state.myLoadsStore['in-transit'].loads;
    case 'completed':
      return store.state.myLoadsStore.completed.loads;
    default:
      return store.state.myLoadsStore.unassigned.loads;
  }
});

const canViewMyLoads = computed(() => {
  return (
    store.state.users.featureFlags?.enable_carrier_loads?.enabled &&
    store.state.users.currentUser?.scopes?.includes('carrier_load_abilities')
  );
});

const canSearchBrokers = computed(
  () => store.state.users.featureFlags?.carrier_can_search_brokers?.enabled
);

// retreive the unassigned loads before component is mounted
// this would be better in the router to set the tab to replace unassigned query with dispatched
// (async () => {
//   const res = await store.dispatch('myLoadsStore/getMyLoads', 'unassigned');
//   if (!res.data.length) {
//     // remove unassigned tab from pageTabs
//     pageTabs.value = {
//       dispatched: { title: 'Covered' },
//       'in-transit': { title: 'In Transit' },
//       completed: { title: 'Completed' },
//     };
//     // set activePageTab to dispatched if activePageTab unassigned
//     if (activePageTab.value === 'unassigned') {
//       activePageTab.value = 'dispatched';
//       router.replace({
//         query: {
//           ...router.currentRoute.value.query,
//           tab: 'dispatched',
//         },
//       });
//     }
//   }
// })();
// watch(
//   () => activePageTab.value,
//   (newValue) => {
//     console.log('activePageTab changed to:', newValue);
//   },
//   { immediate: true }
// );
// watch(
//   () => loads.value,
//   (newValue) => {
//     console.log('loads changed to:', newValue);
//   },
//   { immediate: true }
// );
// watch(
//   () => pagination.value,
//   (newValue) => {
//     console.log('pagination:', newValue);
//   },
//   { immediate: true }
// );

const fetchAll = async () => {
  // Always fetch equipment types
  store.dispatch('loads/getEquipmentTypes');

  // Only fetch myLoadsStore data if canViewMyLoads is true
  if (canViewMyLoads.value) {
    store.dispatch('myLoadsStore/getMyLoads', 'unassigned');
    store.dispatch('myLoadsStore/getMyLoads', 'dispatched');
    store.dispatch('myLoadsStore/getMyLoads', 'in-transit');
    store.dispatch('myLoadsStore/getMyLoads', 'completed');
  }
  if (canSearchBrokers.value) {
    store.dispatch('myLoadsStore/getCarrierBrokers');
  }
};

// const eldCarrierVehicles = computed(() => {
//   return store.state.myLoadsStore.vehicles.eldVehicles;
// });
// helper for checking query object emptiness
const isEmpty = (obj) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }
  return true;
};

const getTabQuery = (tab) => {
  return store.state.myLoadsStore[tab].query;
};
const getIsTabLoading = (tab) => {
  return store.state.myLoadsStore[tab].isLoading;
};
const getTabTotalRecords = (tab) => {
  return store.state.myLoadsStore[tab].pagination.totalRecords;
};

// on page load, check if query exists
if (router.currentRoute.value.query.tab) {
  // if query, save query and fetch data for the current tab
  // set query in store
  store.commit('myLoadsStore/setQuery', {
    tab: activePageTab.value,
    query: router.currentRoute.value.query,
  });
  // fetch all tabs after setting query
  fetchAll();
} else {
  // if no query, trigger query watcher by pushing last query
  router.push({
    query: getTabQuery(activePageTab.value),
  });
}

const hasFetchedAll = computed(
  () =>
    store.state.myLoadsStore.unassigned.hasLoaded &&
    store.state.myLoadsStore.dispatched.hasLoaded &&
    store.state.myLoadsStore['in-transit'].hasLoaded &&
    store.state.myLoadsStore.completed.hasLoaded
);

// on query changes, fetch data
watch(
  () => router.currentRoute.value.query,
  (newValue, oldValue) => {
    if (name.value !== 'My Loads' && name.value !== 'CarrierWelcomePage') {
      return;
    }
    // if no tab, trigger query watcher by pushing last query
    if (isEmpty(newValue) || !newValue.tab) {
      router.push({
        query: getTabQuery(activePageTab.value),
      });
      return;
    }
    // fetch tab data if same tab
    if (newValue.tab === oldValue.tab) {
      store.commit('myLoadsStore/setQuery', {
        tab: newValue.tab,
        query: newValue,
      });
      store.dispatch('myLoadsStore/getMyLoads', newValue.tab);
    } else if (!hasFetchedAll.value) {
      fetchAll();
    }
  }
);
// const handleDataRefreshClick = () => {
//   fetchAll();
// };
const setActivePageTab = (tab) => {
  router.push({
    query: getTabQuery(tab),
  });
};

const isTablet = computed(() => screenSize.value === 'tablet');

const isMobile = computed(() => screenSize.value === 'mobile');

const showFilters = ref(isTablet.value ? false : true);
// const toggleFilters = () => {
//   showFilters.value = !showFilters.value;
// };
const showRefreshButton = ref(false);
const refreshButtonClicks = ref(0);
const handleRefreshButtonClick = () => {
  refreshButtonClicks.value += 1;
  if (refreshButtonClicks.value >= 2) {
    showRefreshButton.value = false;
    refreshButtonClicks.value = 0;
  }
  fetchAll();
};
const handleShowRefreshButton = () => {
  refreshButtonClicks.value = 0;
  setTimeout(() => {
    showRefreshButton.value = true;
  }, 1500);
};

// const filtersCount = computed(
//   () => store.state.myLoadsStore[activePageTab.value].activeFiltersCount
// );

watch(
  () => isTablet.value,
  (newValue) => {
    if (newValue) {
      showFilters.value = false;
    } else {
      showFilters.value = true;
    }
  },
  { immediate: true }
);

const handleCloseFiltersModal = () => {
  if (isTablet.value) {
    showFilters.value = false;
  }
};
</script>

<template>
  <div>
    <MyLoadsMobileMain
      v-if="(name === 'My Loads' && isMobile) || name !== 'My Loads'"
      :page-tabs="pageTabs"
      @connect-eld-click="emit('connectEldClick')"
      @set-active-page-tab="(tab) => setActivePageTab(tab)"
    />
    <div
      v-else
      class="px-10"
    >
      <div class="flex w-full flex-col pt-10">
        <div class="flex w-full items-center justify-between">
          <div class="mr-4 flex w-full items-center justify-between gap-4">
            <h2 class="whitespace-nowrap text-3xl text-dark-gray">My Loads</h2>
            <MyLoadsDesktopButtonComponent
              v-if="showRefreshButton"
              button-class="rounded-[5px] flex items-center gap-2 bg-default-button-bg px-4 py-2 default-button"
              button-type="button"
              button-text="Refresh View"
              text-class="text-sm font-medium text-steel-900"
              @button-click="handleRefreshButtonClick"
            >
              <template #leftIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6.84142 9.39779C7.13019 8.58025 7.59896 7.81146 8.259 7.15518C10.6029 4.81131 14.4018 4.81131 16.7457 7.15518L17.387 7.80021H16.1007C15.4369 7.80021 14.9006 8.33649 14.9006 9.00027C14.9006 9.66406 15.4369 10.2003 16.1007 10.2003H20.2821H20.2971C20.9609 10.2003 21.4972 9.66406 21.4972 9.00027V4.80005C21.4972 4.13627 20.9609 3.59999 20.2971 3.59999C19.6333 3.59999 19.0971 4.13627 19.0971 4.80005V6.12012L18.4408 5.46009C15.1594 2.17867 9.84158 2.17867 6.56016 5.46009C5.64511 6.37514 4.98508 7.45144 4.58006 8.60275C4.35879 9.22903 4.68881 9.91157 5.31134 10.1328C5.93388 10.3541 6.62016 10.0241 6.84142 9.40154V9.39779ZM4.36254 13.8493C4.17504 13.9055 3.99503 14.0068 3.84877 14.1568C3.69876 14.3068 3.59751 14.4868 3.545 14.6818C3.53375 14.7268 3.5225 14.7756 3.515 14.8243C3.50375 14.8881 3.5 14.9518 3.5 15.0156V19.2008C3.5 19.8646 4.03628 20.4009 4.70006 20.4009C5.36385 20.4009 5.90013 19.8646 5.90013 19.2008V17.8845L6.56016 18.5408C9.84158 21.8184 15.1594 21.8184 18.437 18.5408C19.3521 17.6257 20.0159 16.5494 20.4209 15.3981C20.6421 14.7718 20.3121 14.0893 19.6896 13.868C19.0671 13.6468 18.3808 13.9768 18.1595 14.5993C17.8707 15.4169 17.402 16.1856 16.7419 16.8419C14.3981 19.1858 10.5991 19.1858 8.25525 16.8419L8.2515 16.8382L7.61021 16.2006H8.90028C9.56407 16.2006 10.1003 15.6644 10.1003 15.0006C10.1003 14.3368 9.56407 13.8005 8.90028 13.8005H4.71506C4.65506 13.8005 4.59506 13.8043 4.53505 13.8118C4.47505 13.8193 4.4188 13.8305 4.36254 13.8493Z"
                    fill="#6C7C9D"
                  />
                </svg>
              </template>
            </MyLoadsDesktopButtonComponent>
            <MyLoadsDesktopButtonComponent
              button-class="flex items-center default-button rounded-[5px]"
              text-class="text-sm font-medium leadin-loose tracking-tight"
              :button-text="showFilters ? 'Hide Filters' : 'Show Filters'"
              @button-click="showFilters = !showFilters"
            >
              <template #leftIcon>
                <img
                  src="@/assets/filters-icon.svg"
                  alt="filters"
                />
              </template>
            </MyLoadsDesktopButtonComponent>
          </div>
        </div>
        <div class="mb-2 mt-5 flex w-full items-center justify-between">
          <div class="flex w-full items-center gap-8 pt-2">
            <div
              v-for="tab in Object.keys(pageTabs)"
              :key="`${tab}-link`"
              :title="`Change to ${pageTabs[tab]?.title}`"
              class="tab flex cursor-pointer items-center gap-1 whitespace-nowrap rounded text-base leading-tight tracking-tight"
              :class="{
                'text-steel-700': activePageTab !== tab,
                'text-sun-400': activePageTab === tab,
              }"
              @click="setActivePageTab(tab)"
            >
              <div class="flex flex-col">
                <div class="flex gap-1">
                  <div>{{ pageTabs[tab]?.title }}</div>
                  <div
                    :class="[
                      {
                        'skeleton-animation h-4 w-4 rounded-full text-transparent':
                          getIsTabLoading(tab),
                      },
                      getIsTabLoading(tab)
                        ? activePageTab === tab
                          ? 'bg-sun-400'
                          : 'bg-steel-700'
                        : 'bg-white',
                    ]"
                  >
                    {{
                      getIsTabLoading(tab) ? '' : `(${getTabTotalRecords(tab)})`
                    }}
                  </div>

                  <!-- <Transition name="fade">
                  <span v-if="!getIsTabLoading(tab)">{{
                    getTotalRecords(tab)
                  }}</span>
                </Transition> -->
                </div>
                <div
                  v-if="activePageTab === tab && !getIsTabLoading(tab)"
                  class="h-0.5 w-full rounded-sm bg-sun-400"
                ></div>
              </div>
              <!-- <div
              class="flex h-7 items-center justify-between gap-2 text-sm leading-7"
            >
              <div
                v-if="isLoading"
                title="Fetching data"
              >
                <svg
                  class="icon loading hover:text-yellow-400"
                  :class="{ loading: isLoading }"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.84142 9.39798C7.13019 8.58043 7.59896 7.81165 8.259 7.15536C10.6029 4.81149 14.4018 4.81149 16.7457 7.15536L17.387 7.80039H16.1007C15.4369 7.80039 14.9006 8.33667 14.9006 9.00046C14.9006 9.66424 15.4369 10.2005 16.1007 10.2005H20.2821H20.2971C20.9609 10.2005 21.4972 9.66424 21.4972 9.00046V4.80024C21.4972 4.13645 20.9609 3.60018 20.2971 3.60018C19.6333 3.60018 19.0971 4.13645 19.0971 4.80024V6.12031L18.4408 5.46027C15.1594 2.17885 9.84158 2.17885 6.56016 5.46027C5.64511 6.37532 4.98508 7.45163 4.58006 8.60294C4.35879 9.22922 4.68881 9.91175 5.31134 10.133C5.93388 10.3543 6.62016 10.0243 6.84142 9.40173V9.39798ZM4.36254 13.8495C4.17504 13.9057 3.99503 14.007 3.84877 14.157C3.69876 14.307 3.59751 14.487 3.545 14.682C3.53375 14.727 3.5225 14.7758 3.515 14.8245C3.50375 14.8883 3.5 14.952 3.5 15.0158V19.201C3.5 19.8648 4.03628 20.4011 4.70006 20.4011C5.36385 20.4011 5.90013 19.8648 5.90013 19.201V17.8847L6.56016 18.541C9.84158 21.8186 15.1594 21.8186 18.437 18.541C19.3521 17.6259 20.0159 16.5496 20.4209 15.3983C20.6421 14.772 20.3121 14.0895 19.6896 13.8682C19.0671 13.6469 18.3808 13.977 18.1595 14.5995C17.8707 15.417 17.402 16.1858 16.7419 16.8421C14.3981 19.186 10.5991 19.186 8.25525 16.8421L8.2515 16.8384L7.61021 16.2008H8.90028C9.56407 16.2008 10.1003 15.6646 10.1003 15.0008C10.1003 14.337 9.56407 13.8007 8.90028 13.8007H4.71506C4.65506 13.8007 4.59506 13.8045 4.53505 13.812C4.47505 13.8195 4.4188 13.8307 4.36254 13.8495Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div> -->
            </div>
          </div>
        </div>
      </div>
      <div class="flex w-full items-center gap-8">
        <!-- <MyLoadsDesktopButtonComponent
        button-class="flex items-center default-button rounded-[5px]"
        text-class="text-sm font-medium leadin-loose tracking-tight"
        :button-text="showFilters ? 'Hide Filters' : 'Show Filters'"
        @button-click="showFilters = !showFilters"
      >
        <template #leftIcon>
          <img
            src="@/assets/filters-icon.svg"
            alt="filters"
          />
        </template>
      </MyLoadsDesktopButtonComponent> -->
        <!-- <button
        v-if="filtersCount > 0"
        class="mt-1 flex min-w-max items-center gap-1 rounded-md bg-gray-200 px-3 py-1 text-sm"
        @click="handleFilterReset"
      >
        <svg
          class="ml-1 h-4 w-4"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
            clip-rule="evenodd"
          ></path>
        </svg>
        Reset Filters
      </button> -->
      </div>
      <!-- <FiltersBar
        :show-filters="showFilters"
        @toggle-filters="toggleFilters"
      /> -->
      <!-- flex container -->
      <div
        class="mt-4 flex w-full"
        :class="isTablet ? 'gap-0' : 'gap-8'"
      >
        <TeleportModalWrapperComponent
          v-if="showFilters"
          :should-teleport="isTablet"
          :is-modal-open="showFilters"
        >
          <MyLoadsDesktopFiltersComponent
            v-if="showFilters"
            :active-tab="activePageTab"
            :is-loading="isLoading"
            @close-modal="handleCloseFiltersModal"
          />
        </TeleportModalWrapperComponent>
        <!-- results section -->
        <div
          class="w-full overflow-auto rounded-md pb-4"
          :class="showFilters ? 'flex-1' : 'w-full'"
        >
          <!-- no data -->
          <div
            v-if="!loads.length || isLoading"
            class="flex h-96 justify-center rounded-lg bg-off-white"
          >
            <div
              class="inner-container flex flex-col items-center justify-center p-8"
            >
              <div
                v-if="isLoading"
                class="mx-auto h-16 w-16 text-center"
              >
                <img
                  class="loading relative w-full"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading"
                />
              </div>
              <div
                v-else
                class="magnify-image"
              >
                <img
                  class="-top-2px relative w-full text-yellow-500"
                  src="@/assets/magnify.svg"
                  alt=""
                />
              </div>
              <div class="mt-4 text-gray-over-dark">
                <h3
                  v-if="isLoading"
                  class="text-center text-lg"
                >
                  Loading
                </h3>
                <h3
                  v-else
                  class="text-center text-lg"
                >
                  No results found for
                  {{ pageTabs[activePageTab]?.title.toLowerCase() }}.
                </h3>
              </div>
            </div>
          </div>
          <!-- data -->
          <MyLoadsDesktopTableComponent
            v-else
            @show-refresh-button="handleShowRefreshButton"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
